import React, { FC, ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';
import OvPageTitle from '../molecules/OvPageTitle';
import OvPaginationBar from '../molecules/OvPaginationBar';

interface OvRecordListPageProps {
  title: string;
  icon: ReactElement;
  resultSummary: ReactNode;
  total: number;
  limit: number;
  offset: number | null;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  children: ReactNode;
  headerAction?: ReactNode;
  filters?: ReactNode;
  context?: ReactNode;
  isBusy?: boolean;
}

const OvRecordListPage: FC<OvRecordListPageProps> = ({
  title,
  icon,
  resultSummary,
  total,
  limit,
  offset,
  onPageChange,
  children,
  headerAction,
  filters,
  context,
  isBusy = false,
}) => {
  const { t } = useTranslation();

  return (
    <Panel aria-busy={isBusy}>
      <Header>
        <OvPageTitle title={title} icon={icon} />
        {headerAction && <HeaderActions>{headerAction}</HeaderActions>}
      </Header>

      {filters}
      {context}

      <ResultSummary role="status">{resultSummary}</ResultSummary>

      {children}

      <Footer>
        <PerPage>{t('common.listResults.perPage', { count: limit })}</PerPage>
        <OvPaginationBar
          total={total}
          limit={limit}
          offset={offset}
          onChange={onPageChange}
        />
      </Footer>

      {isBusy && <OvLoadingIndicator position="fixed" />}
    </Panel>
  );
};

export default OvRecordListPage;

const Panel = styled.section`
  margin-left: ${Variables.spacing.MEDIUM};
  min-width: 0;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: ${Variables.spacing.XLARGE};
  border-radius: ${Variables.borderRadius.LARGE};

  @media (max-width: ${breakpoints.sm}) {
    padding: ${Variables.spacing.MEDIUM};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${Variables.spacing.LARGE};
  margin-bottom: ${Variables.spacing.LARGE};

  a {
    text-decoration: none;
  }
`;

const ResultSummary = styled.p`
  margin: ${Variables.spacing.MEDIUM} 0 ${Variables.spacing.SMALL};
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.MEDIUM};
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${Variables.spacing.SMALL};
  margin-left: auto;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Variables.spacing.LARGE};
  margin-top: ${Variables.spacing.LARGE};
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};

  @media (max-width: ${breakpoints.sm}) {
    justify-content: center;
  }
`;

const PerPage = styled.span`
  @media (max-width: ${breakpoints.sm}) {
    display: none;
  }
`;
